import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import LuminartLogo from "../components/LuminartLogo";
import ProjectCard from "../components/ProjectCard";
import WorkLightbox from "../components/WorkLightbox";
import Footer from "../components/Footer";
import { useContent } from "../lib/content";

const ease = [0.16, 1, 0.3, 1];

export const ProjectsPage = () => {
  const [open, setOpen] = useState(null);
  const { projects: PROJECTS } = useContent();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0c]" data-testid="projects-page">
      {/* Slim archive header — main navbar stays exclusive to the home page */}
      <header className="sticky top-0 z-40 flex items-center justify-between px-6 md:px-12 lg:px-16 h-20 border-b border-white/10 bg-[#0a0a0c]/85 backdrop-blur-md">
        <Link
          to="/"
          data-testid="archive-home-link"
          className="flex items-center gap-3 group"
        >
          <LuminartLogo className="w-9 h-auto" glow={false} />
          <span className="font-heading text-sm tracking-[0.35em] uppercase text-white/90 group-hover:text-white transition-colors hidden sm:block">
            Luminart
          </span>
        </Link>
        <Link
          to="/"
          data-testid="archive-back-link"
          className="group inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white/50 hover:text-white transition-colors"
        >
          <ArrowLeft
            size={13}
            className="group-hover:-translate-x-1 transition-transform duration-300"
          />
          Back to Home
        </Link>
      </header>

      <main className="px-6 md:px-12 lg:px-16 py-16 md:py-24">
        <div className="flex items-end justify-between gap-8">
          <div>
            <span className="text-[11px] uppercase tracking-[0.3em] text-white/85">
              All Projects
            </span>
            <div className="mt-2 h-px w-9 bg-[#c8b99d]" />
            <h1 className="mt-8 font-heading text-4xl sm:text-5xl lg:text-6xl font-extralight tracking-tight text-white">
              Everything we brought
              <br />
              <span className="italic text-[#c8b99d]">to life.</span>
            </h1>
          </div>
          <span className="hidden md:block text-[10px] uppercase tracking-[0.3em] text-white/30 pb-2">
            ({String(PROJECTS.length).padStart(2, "0")}) Projects
          </span>
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {PROJECTS.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.9, delay: (i % 3) * 0.08, ease }}
            >
              <ProjectCard p={p} i={i} onOpen={setOpen} className="w-full" />
            </motion.div>
          ))}
        </div>

        <p className="mt-16 text-center text-[10px] uppercase tracking-[0.3em] text-white/30">
          More campaigns in production — follow{" "}
          <a
            href="https://www.instagram.com/luminart.projection/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="archive-instagram-link"
            className="text-[#c8b99d]/80 hover:text-[#c8b99d] transition-colors"
          >
            @luminart.projection
          </a>
        </p>
      </main>

      <Footer />

      <WorkLightbox
        project={open !== null ? PROJECTS[open] : null}
        index={open ?? 0}
        total={PROJECTS.length}
        onClose={() => setOpen(null)}
      />
    </div>
  );
};

export default ProjectsPage;
